// ====================================================================
// CLOUDS — soft billboards drifting high over the meadow
// ====================================================================
// The sky was a flat color; a few puffs give it life for almost nothing:
// each cloud is one sprite (LOW: 3 clouds, mid+: 6), they drift on a shared
// wind, wrap at the world edge, and dim with the day/night cycle through one
// shared material. No per-frame allocation.
import * as THREE from 'three';
import { LOW, cachedTexture } from '../core/tier.js';
import { TUNING } from '../core/tuning.js';
import { makeCanvasTexture } from '../core/texture.js';
import { markDynamic } from './freezeStatics.js';

const clouds = [];
const cloudMats = [];
let lastBright = -1;

function makeCloudTexture(variant = 0) {
  return makeCanvasTexture(256, 128, (ctx) => {
  // A handful of overlapping soft blobs, denser in the middle, baked once.
  const blob = (x, y, r, a) => {
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, `rgba(255,255,255,${a})`);
    g.addColorStop(0.7, `rgba(255,255,255,${a * 0.55})`);
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
  };
  const variants = [
    [[128, 78, 64, 0.9], [82, 84, 44, 0.85], [176, 86, 46, 0.85], [110, 58, 38, 0.8], [152, 60, 34, 0.8]],
    [[118, 82, 58, 0.88], [70, 90, 36, 0.78], [162, 72, 52, 0.82], [198, 92, 34, 0.7], [125, 54, 34, 0.74]],
    [[138, 84, 54, 0.86], [96, 74, 46, 0.82], [184, 78, 44, 0.78], [140, 56, 42, 0.76], [58, 96, 24, 0.52]],
    [[118, 76, 50, 0.84], [88, 82, 42, 0.78], [154, 70, 58, 0.8], [200, 82, 42, 0.62], [135, 54, 32, 0.62]],
  ];
  for (const b of variants[variant % variants.length]) blob(...b);
  // A baked warm-grey underside makes every billboard read less like a flat decal;
  // still one cached texture and zero per-frame work.
  const shade = ctx.createLinearGradient(0, 50, 0, 128);
  shade.addColorStop(0, 'rgba(180,190,205,0)');
  shade.addColorStop(1, 'rgba(128,142,166,0.18)');
  ctx.globalCompositeOperation = 'source-atop';
  ctx.fillStyle = shade;
  ctx.fillRect(0, 0, 256, 128);
  ctx.globalCompositeOperation = 'source-over';
  });
}

export function initClouds(parent) {
  // The sky is a one-time boot set. Guard the whole builder against a second
  // call: only the base materials below were guarded, so a re-init used to
  // duplicate every sprite and leak a fresh batch of cloned wisp materials.
  if (clouds.length) return;
  if (!cloudMats.length) {
    for (let i = 0; i < 4; i++) {
      cloudMats.push(new THREE.SpriteMaterial({
        map: cachedTexture(`cloud-${i}`, () => makeCloudTexture(i)),
        transparent: true,
        opacity: 0.8,
        depthWrite: false,
        fog: false,         // clouds live above the haze, not inside it
      }));
    }
  }
  const N = TUNING.cloudCount ?? (LOW ? 3 : 6);
  for (let i = 0; i < N; i++) {
    const sprite = new THREE.Sprite(cloudMats[i % cloudMats.length]);
    // Low and far: they hug the fog horizon, so they read in every camera —
    // the default diorama looks downward and would never see a high sky.
    const a = (i / N) * Math.PI * 2 + Math.random() * 0.8;
    const r = 38 + Math.random() * 20;
    sprite.position.set(Math.cos(a) * r, 12 + Math.random() * 7, Math.sin(a) * r);
    const w = 13 + Math.random() * 9;
    sprite.scale.set(w * (Math.random() < 0.5 ? -1 : 1), w * (0.34 + Math.random() * 0.18), 1);
    sprite.center.set(0.5 + (Math.random() - 0.5) * 0.08, 0.5 + (Math.random() - 0.5) * 0.05);
    markDynamic(sprite);   // updateClouds drifts it
    parent.add(sprite);
    clouds.push({ sprite, vx: 0.25 + Math.random() * 0.35, baseOpacity: 0.8 });
  }
  // A second, slower parallax band of thin high clouds gives the sky more
  // storybook depth. LOW swaps in just one wisp so the transparent overdraw
  // budget stays almost identical to the original single-band sky.
  const W = TUNING.wispCount ?? (LOW ? 1 : 3);
  for (let i = 0; i < W; i++) {
    const wispMat = cloudMats[(i + 2) % cloudMats.length].clone();
    cloudMats.push(wispMat);
    const sprite = new THREE.Sprite(wispMat);
    const a = (i / W) * Math.PI * 2 + 0.45;
    const r = 46 + Math.random() * 16;
    sprite.position.set(Math.cos(a) * r, 19 + Math.random() * 4, Math.sin(a) * r);
    const w = 22 + Math.random() * 9;
    sprite.scale.set(w * (Math.random() < 0.5 ? -1 : 1), w * 0.16, 1);
    sprite.material.opacity = 0.46;
    markDynamic(sprite);   // updateClouds drifts it
    parent.add(sprite);
    clouds.push({ sprite, vx: 0.08 + Math.random() * 0.10, baseOpacity: 0.46 });
  }
}

export function updateClouds(dt, nightFactor) {
  for (const c of clouds) {
    c.sprite.position.x += c.vx * dt;
    if (c.sprite.position.x > 72) c.sprite.position.x = -72;   // wrap with the wind
  }
  // Dim toward a moonlit grey at night; write only on real change.
  const bright = 1 - nightFactor * 0.65;
  if (Math.abs(bright - lastBright) > 0.02) {
    lastBright = bright;
    for (const mat of cloudMats) {
      mat.color.setScalar(bright);
    }
    for (const c of clouds) {
      c.sprite.material.opacity = c.baseOpacity * (1 - nightFactor * 0.44);
    }
  }
}
